import React, { useState, useContext } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import './OfficeAnnouncements.scss';

import { OfficeContext } from './Office';
import { createOfficeAnnouncement, updateOfficeAnnouncement, deleteOfficeAnnouncement } from '../../api/office';

const AnnouncementForm = ({ announcement, onSave, onCancel }) => {
  const [date, setDate] = useState(announcement ? moment(announcement.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'));
  const [title, setTitle] = useState(announcement ? announcement.title : '');
  const [description, setDescription] = useState(announcement ? announcement.description : '');
  const [allowEarlyBooking, setAllowEarlyBooking] = useState(announcement ? announcement.allowEarlyBooking : false);

  const handleSubmit = e => {
    e.preventDefault();
    onSave({ date, title, description, allowEarlyBooking });
  };

  return (
    <div className='announcement-form-overlay'>
      <div className='announcement-form'>
        <h3>{announcement ? 'Edit Announcement' : 'New Announcement'}</h3>
        <form onSubmit={handleSubmit}>
          <div className='form-group'>
            <label>Date</label>
            <input type='date' value={date} onChange={e => setDate(e.target.value)} required />
          </div>
          <div className='form-group'>
            <label>Title</label>
            <input type='text' value={title} onChange={e => setTitle(e.target.value)} required placeholder='Team Colocation' />
          </div>
          <div className='form-group'>
            <label>Details (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder='The kitchen is reserved after 5:30pm' />
          </div>
          <div className='form-group checkbox-group'>
            <label className='checkbox-label'>
              <input type='checkbox' checked={allowEarlyBooking} onChange={e => setAllowEarlyBooking(e.target.checked)} />
              Allow early booking
            </label>
          </div>
          <div className='form-actions'>
            <button type='button' className='cancel-btn' onClick={onCancel}>
              Cancel
            </button>
            <button type='submit' className='save-btn'>
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OfficeAnnouncements = () => {
  const ctx = useContext(OfficeContext);
  const { isAdmin, announcements, announcementsLoading: loading, fetchAnnouncements } = ctx;
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const handleDelete = async announcement => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await deleteOfficeAnnouncement(announcement.id);
      window.ALERT.success('Announcement deleted');
      fetchAnnouncements();
    } catch (err) {
      window.ALERT.error(err || 'Failed to delete announcement.');
    }
  };

  const handleSave = async data => {
    try {
      if (editingAnnouncement) {
        await updateOfficeAnnouncement(editingAnnouncement.id, data);
        window.ALERT.success('Announcement updated');
      } else {
        await createOfficeAnnouncement(data);
        window.ALERT.success('Announcement created');
      }
      setShowForm(false);
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (err) {
      window.ALERT.error(err || 'Failed to save announcement.');
    }
  };

  const openEdit = announcement => {
    setEditingAnnouncement(announcement);
    setShowForm(true);
  };

  const openCreate = () => {
    setEditingAnnouncement(null);
    setShowForm(true);
  };

  if (loading) return <div className='office-announcements loading'>Loading...</div>;

  const grouped = _.groupBy(announcements, a => moment.utc(a.date).format('YYYY-MM-DD'));
  const sortedDates = Object.keys(grouped).sort();
  const todayStr = moment().format('YYYY-MM-DD');

  return (
    <div className='office-announcements'>
      <div className='announcements-header'>
        <div className='announcements-title'>Announcements</div>
        {isAdmin && (
          <button className='create-btn' onClick={openCreate}>
            + New Announcement
          </button>
        )}
      </div>

      {sortedDates.length === 0 && (
        <div className='announcements-empty'>
          <div className='empty-text'>No upcoming announcements</div>
        </div>
      )}

      <div className='announcements-timeline'>
        {sortedDates.map(date => {
          const dayAnnouncements = _.sortBy(grouped[date], 'id');
          const isToday = date === todayStr;
          const dateFormatted = moment.utc(date).format('dddd, MMM D');

          return (
            <div key={date} className={cn('timeline-day', { today: isToday })}>
              <div className='day-header'>
                <div className='day-dot' />
                <div className='day-label'>
                  {isToday ? <span className='day-today'>Today</span> : null}
                  <span className='day-date'>{dateFormatted}</span>
                </div>
              </div>

              <div className='day-announcements'>
                {dayAnnouncements.map(a => (
                  <div key={a.id} className='announcement-card'>
                    <div className='announcement-details'>
                      <div className='announcement-name'>{a.title}</div>
                      <div className='announcement-desc'>{a.description}</div>
                    </div>

                    {isAdmin && (
                      <div className='announcement-actions'>
                        <div className='announcement-action-btns'>
                          <button className='edit-btn' onClick={() => openEdit(a)}>
                            Edit
                          </button>
                          <button className='edit-btn' onClick={() => handleDelete(a)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <AnnouncementForm
          announcement={editingAnnouncement}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingAnnouncement(null);
          }}
        />
      )}
    </div>
  );
};

export default OfficeAnnouncements;
